import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';

import { useCursorContext } from '$contexts/cursor.jsx';

const CURSOR_DORMANT_TIME = 2000; // 2s
const INTERACTIVE_ELEMENT_SELECTOR = 'a, button, input, textarea';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypes>>}
 *
 *
 * This cursor is displayed all the time by default, or you can registerAreas to only display the cursor in those.
 * It has a focused state when hovering on interactiveElement, which can be conbined with the mode state.
 *
 * 3. Tracks the interactive elements and focus the cursor if the mouse
 *    is over one of them.
 * 3.1. Detects when the active element has been left due to page reflows or
 *     programatic display manipulations.
 * 4. If the cursor is displayed tracks the position of the mouse
 *    and replicates on the custom cursor.
 * 5. Tracks each area registered in the context and display the cursor only
 *    in those areas.
 */
const Cursor = () => {
    const { state } = useCursorContext();

    const [labelContent, setLabelContent] = useState();
    const [cursorPosition, setCursorPosition] = useState({ x: 0, y: 0 });
    const [isCursorDormant, setIsCursorDormant] = useState(false);
    const [isCursorFocused, setIsCursorFocused] = useState(false);
    const [isCursorHidden, setIsCursorHidden] = useState(true);

    const sensorDetect = (e) => {
        // Prevent cursor display on load
        if (isCursorHidden) {
            setIsCursorHidden(false);
        }

        /* [3] */
        const isInteractiveElement = e.target.matches(INTERACTIVE_ELEMENT_SELECTOR) ||
            e.target.closest(INTERACTIVE_ELEMENT_SELECTOR) ? true : false;

        if (isInteractiveElement) {
            setIsCursorFocused(true);

            const mouseleaveListener = e.target.addEventListener('mouseleave', () => {
                setIsCursorFocused(false);
                window.removeEventListener('mouseleave', mouseleaveListener);
            });

            e.target.addEventListener('click', (e) => {
                setTimeout(() => {
                    const hasLeft = !e.target.matches('*:hover');
                    hasLeft && setIsCursorFocused(false); /* [3.1] */

                    window.removeEventListener('mouseleave', mouseleaveListener);
                }, 50);
            });
        }
    };

    /* [4] */
    useEffect(() => {
        if (!isCursorHidden) { return; }

        let mouseX = 0;
        let mouseY = 0;
        let lastMoved = null;
        let cursorStopped = false;

        const frame = () => {
            setCursorPosition({ x: mouseX, y: mouseY });
            window.requestAnimationFrame(frame);
            cursorStopped = lastMoved && ((Date.now() - lastMoved) > CURSOR_DORMANT_TIME);
            setIsCursorDormant(cursorStopped);
        };
        frame();

        const syncCursorPosition = (e) => {
            if (mouseX !== e.clientX || mouseY !== e.clientY) {
                mouseX = e.clientX;
                mouseY = e.clientY;
                lastMoved = Date.now();
            }
        };

        addEventListener('mousemove', syncCursorPosition);
        return () => {
            removeEventListener('mousemove', syncCursorPosition);
            window.cancelAnimationFrame(frame);
        };
    }, []);

    useEffect(() => {
        addEventListener('mouseover', sensorDetect);

        let cleanups = [() => {
            removeEventListener('mouseover', sensorDetect);
        }];

        /* [5] */
        if (state.areas.length) {
            cleanups = state.areas.map(({
                ref,
                labelContent = null
            }) => {
                const setCursorConfiguration = () => {
                    setIsCursorHidden(false);
                    setLabelContent(labelContent);
                };

                const clearCursorConfiguration = () => {
                    setLabelContent(null);
                };

                ref.current?.addEventListener('mouseenter', setCursorConfiguration);
                ref.current?.addEventListener('mouseleave', clearCursorConfiguration);
                ref.current?.addEventListener('mouseover', sensorDetect);

                return () => {
                    ref.current?.removeEventListener('mouseenter', setCursorConfiguration);
                    ref.current?.removeEventListener('mouseleave', clearCursorConfiguration);
                    ref.current?.removeEventListener('mouseover', sensorDetect);
                };
            });
        }

        return () => {
            cleanups.forEach(cleanup => cleanup());
        };
    }, [ state.areas, labelContent ]);

    if (isCursorHidden) { return null; }

    return (
        <>
            <div
                aria-hidden='true'
                className={`cursor__wrapper${isCursorFocused ? ' cursor--focus' : ''}${isCursorHidden ? ' cursor--hidden' : ''}${isCursorDormant ? ' cursor--dormant' : ''}`}
                style={{ transform: `translate(${cursorPosition.x}px, ${cursorPosition.y}px) translate(-50%, -50%)` }}>
                <div className='cursor__icon' />
            </div>

            <CursorLabel
                {...cursorPosition}
                content={labelContent}
                visible={Boolean(labelContent)}>
            </CursorLabel>
        </>
    );
};

/**
 * @type {React.FC<PropTypes.InferProps<typeof propTypesLabel>>}
 */
const CursorLabel = ({
    content = null,
    visible = false,
    x = 0,
    y = 0
}) => {
    const [ movement, setMovement ] = useState({
        // rotation: 5,
        x: 0,
        y: 0
    });

    const contentRef = useRef(null);
    contentRef.current = content || contentRef.current;

    useEffect(() => {
        let movementX = 0;
        let movementY = 0;

        const frame = () => {
            const setIntLimits = ({ int, min, max }) => Math.min(Math.max(min, int), max);
            const isVertical = Math.abs(movementY) > Math.abs(movementX);
            const x = setIntLimits({ int: movementX, min: -5, max: 5 });
            const y = setIntLimits({ int: movementY, min: -5, max: 5 });
            const newRotation = (x + y) * (isVertical ? 1 : .5);

            setMovement({
                // roation: newRotation,
                x: x,
                y: y
            });

            window.requestAnimationFrame(frame);
        };

        frame();

        const setMouseParameters = (e) => {
            if (movementX !== e.movementX || movementY !== e.movementY) {
                movementX = e.movementX;
                movementY = e.movementY;
            }
        };

        addEventListener('mousemove', setMouseParameters);
        return () => {
            removeEventListener('mousemove', setMouseParameters);
            window.cancelAnimationFrame(frame);
        };
    }, []);

    return (
        <div
            aria-hidden='true'
            className='cursor__label-wrapper'
            style={{
                opacity: visible ? 1 : 0,
                transform: `translate(${x}px, ${y}px) translate(0%, -50%)`
            }}>
            <div
                className='cursor__label'
                style={{ transform: `translate(${movement.x * -1}px, ${movement.y * -1}px) rotate(${movement.rotation * -1}deg)` }}>
                {contentRef.current}
            </div>
        </div>
    );
};

/* ========================================================================= */
/* Typing and export(s) */
/* ========================================================================= */

const propTypes = {};
Cursor.propTypes = propTypes;

const propTypesLabel = {
    content: PropTypes.any,
    visible: PropTypes.bool,
    x: PropTypes.number,
    y: PropTypes.number
};
CursorLabel.propTypes = propTypesLabel;

export default Cursor;
